import { parseStringToBoolean, twClassMerge } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { USER_PROFILE_DEFAULT_NAME } from '@shared/constants/constants';
import { USE_ENETS_PAYMENT } from '@shared/constants/systemSettingsKey';
import { useSystemSettingsValue } from '@shared/hooks/useSystemSettingsValue';
import { getDefaultProfilePicDisplay, getProfilePictureS3ImageDisplay } from '@shared/utils/profile';

import AccountSettingsDropdown from './AccountSettingsDropdown';

const { Text, Title } = Typography;

interface ProfileProps {
  className?: string;
  description: string;
  linkedPaymentMethodUrl: string;
  manageProfileUrl: string;
  title?: string;
  userProfile: {
    name?: null | string;
    photo?: null | string;
  };
}

const DashboardProfile = ({
  className,
  description,
  manageProfileUrl,
  linkedPaymentMethodUrl,
  title,
  userProfile,
}: ProfileProps) => {
  const { t } = useTranslation('home');

  const { getValue } = useSystemSettingsValue([USE_ENETS_PAYMENT]);
  const isENetsPaymentEnabled = parseStringToBoolean(getValue(USE_ENETS_PAYMENT));

  return (
    <div className={twClassMerge('flex flex-col justify-between gap-6 md:flex-row md:items-center', className)}>
      {!!title && (
        <div className="flex flex-col gap-2">
          <Title className="m-0 text-3xl text-primary-blue-grey-4 lg:text-4.5xl" level={1}>
            {title}
          </Title>

          <Text className="text-sm text-primary-blue-grey lg:text-base">{description}</Text>
        </div>
      )}

      <div className="flex gap-2">
        <img
          alt=""
          className="gbomit size-20px shrink-0 rounded-[24px] object-cover"
          data-testid="profile-picture"
          onError={(e) => {
            e.currentTarget.src = getDefaultProfilePicDisplay(null, null);
          }}
          src={getProfilePictureS3ImageDisplay(userProfile.photo ?? '', null, null)}
        />

        <div className="flex flex-col gap-0.5">
          <Text className="gbmask line-clamp-1 max-w-60 text-base font-semibold">
            {userProfile.name || USER_PROFILE_DEFAULT_NAME}
          </Text>

          <AccountSettingsDropdown
            header={t('accountSettings')}
            menu={{
              items: [
                {
                  key: 'profile',
                  label: <Link to={manageProfileUrl}>{t('manageProfile')}</Link>,
                },
                ...(isENetsPaymentEnabled
                  ? []
                  : [
                      {
                        key: 'payment',
                        label: <Link to={linkedPaymentMethodUrl}>{t('linkedPaymentMethod')}</Link>,
                      },
                    ]),
              ],
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardProfile;
